import React from 'react';
import { Page, Text, View, Document, Image, Link, Svg, Path } from '@react-pdf/renderer';
import { useProcessedReportData } from "./useProcessedReportData";
import styles from './styles/customFormPDFStyles';
import { formTypes } from '../formBuilder/formBuilderConsts';

const MaxImagesPerPage = 21;

const PageHeight = 1120;
const PagePadding = 40;
const HeroHeight = 321;
const SectionsPerSummaryRow = 3;
const SummaryRowHeight = 74;
const SummarySectionPadding = 15;
const SectionTitleHeight = 48;
const SectionBottomPadding = 18;
const SectionBottomMargin = 21;
const QuestionBaseHeight = 32;
const QuestionLineHeight = 15;
const AvgCharactersPerQuestionLine = 37;
const ImageBaseHeight = 107;
const ImageCaptionBaseHeight = 12;
const ImageCaptionLineHeight = 12;
const AvgCharactersPerImageCaptionLine = 41;

function chunk(arr, size) {
    return Array.from({ length: Math.ceil(arr?.length / size) }, (v, i) =>
        arr.slice(i * size, i * size + size)
    );
}

const ChecklistIcon = ({ question }) => {
    if (question.status === 'fail') {
        return (
            <View style={styles.icon}>
                <Svg viewBox="0 0 24 24">
                    <Path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" fill="#d32f2f" />
                </Svg>
            </View>
        );
    }
    if (!!question.answer) {
        return (
            <View style={styles.icon}>
                <Svg viewBox="0 0 24 24">
                    <Path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#0e67c2" />
                </Svg>
            </View>
        );
    }
    return (
        <View style={styles.icon}>
            <Svg viewBox="0 0 24 24">
                <Path d="M19 13H5v-2h14v2z" fill="#0e67c2" />
            </Svg>
        </View>
    );
};

const SectionPart = ({ sectionPart }) => (
    <View style={styles.section}>
        <Text style={styles.sectionTitle}>{sectionPart.name}</Text>
        <View style={styles.sectionBody}>
            <View style={styles.imagesContainer}>
                {sectionPart.images.map((img, idx) => (
                    <View key={idx} style={styles.imageWrapper}>
                        <Link src={img.url} style={{ textDecoration: 'none' }}>
                            <Image style={styles.image} src={img.url} />
                            <Text style={styles.imageCaption}>{img.type}</Text>
                        </Link>
                    </View>
                ))}
            </View>
            <View style={styles.checklistContainer}>
                {sectionPart.questions.map(q => (
                    <View key={q.id} style={styles.checklistItem}>
                        <ChecklistIcon question={q} />
                        <View style={styles.checklistItemContent}>
                            <View style={styles.checklistItemHeader}>
                                <Text style={styles.questionText}>{q.question}</Text>
                                {q.status === 'fail' && <Text style={styles.failChip}>Fail</Text>}
                            </View>
                            <Text style={q.status === 'fail' ? { ...styles.answerText, ...styles.answerTextFail } : styles.answerText}>{q.displayValue}</Text>
                        </View>
                    </View>
                ))}
            </View>
        </View>
    </View>
);

const getAvailablePageHeight = () => PageHeight - (PagePadding * 2 + SectionTitleHeight + SectionBottomPadding);

function calcSummarySectionHeight(numberOfSections) {
    let numberOfRows = Math.floor(numberOfSections / SectionsPerSummaryRow);
    return numberOfRows * SummaryRowHeight + SummarySectionPadding;
}

function calcQuestionHeight(questionLength) {
    let numberOfLines = questionLength > 0 ? Math.ceil(questionLength / AvgCharactersPerQuestionLine) : 0;
    return QuestionBaseHeight + QuestionLineHeight * numberOfLines;
}

function calcImageHeight(questionLength) {
    let numberOfLines = Math.ceil(questionLength / AvgCharactersPerImageCaptionLine);
    return ImageBaseHeight + ImageCaptionBaseHeight + ImageCaptionLineHeight * numberOfLines;
}

function calcSectionPartHeight(questions) {
    const questionsHeight = questions?.reduce((acc, q) => acc + calcQuestionHeight(q.question?.length ?? 0), 0);

    let questionsWithImages = questions?.filter(q => !!q.answer?.pictureUrl);
    let imagesHeight = 0;
    let lastImageHeight = 0;

    for (let i = 0; i < questionsWithImages?.length; i++) {
        let imageHeight = calcImageHeight(questionsWithImages[i]?.question?.length)

        if (i % 2 === 0) {
            imagesHeight += imageHeight;
        } else if (imageHeight > lastImageHeight) {
            imagesHeight += imageHeight - lastImageHeight;
        }

        lastImageHeight = imageHeight;
    }

    return SectionTitleHeight + Math.max(questionsHeight, imagesHeight);
}

function getPaginatedSectionParts(sections) {
    let summarySectionHeight = calcSummarySectionHeight(sections?.length);
    let remainingHeight = PageHeight - (HeroHeight + summarySectionHeight + PagePadding * 2 + SectionBottomPadding + SectionTitleHeight);

    let pages = [];
    pages.push([]);

    for (let sectionIndex = 0; sectionIndex < sections?.length; sectionIndex++) {
        let section = sections?.[sectionIndex] ?? {};

        const newSectionPart = () => ({ name: section.name, questions: [], images: [], remainingHeight, totalQuestionHeight: 0 });

        let pageSectionParts = [newSectionPart()];

        for (let questionIndex = 0; questionIndex < section.questions?.length; questionIndex++) {
            let question = section.questions[questionIndex];
            let questionLength = question.question?.length || 1;
            let questionHeight = calcQuestionHeight(questionLength);

            let lastSectionPart = pageSectionParts[pageSectionParts?.length - 1];
            let heightWithQuestion = calcSectionPartHeight([...lastSectionPart.questions, question]);

            if (heightWithQuestion + SectionBottomPadding > lastSectionPart.remainingHeight) {
                if (pageSectionParts.find(p => !!p.questions.length)) {
                    let currentPage = pages[pages.length - 1];
                    pages[pages.length - 1] = [...currentPage, ...pageSectionParts];
                }

                pages.push([]);

                remainingHeight = getAvailablePageHeight();
                pageSectionParts = [newSectionPart()];
            }

            lastSectionPart = pageSectionParts[pageSectionParts.length - 1];
            lastSectionPart.questions.push(question);
            if (question.answer?.pictureUrl) {
                lastSectionPart.images.push({ url: question.answer.pictureUrl, type: question.question });
            }

            remainingHeight -= questionHeight;
        }

        let currentPage = pages[pages.length - 1];
        pages[pages.length - 1] = [...currentPage, ...pageSectionParts];

        remainingHeight -= SectionBottomPadding + SectionBottomMargin + SectionTitleHeight;
    }

    return pages;
}

const CustomFormOutputPDF = ({
    vehicle,
    answers,
    sections,
    imageTypes,
    generatedDate,
    lot,
    formTypeLabel,
    showFailuresOnly,
    formType
}) => {
    const { metadata, sections: processedSections, heroImageObject, generalVehicleImages } = useProcessedReportData(vehicle, answers, sections, imageTypes, generatedDate, lot, showFailuresOnly);
    const imagePages = chunk(generalVehicleImages, MaxImagesPerPage);

    const paginatedSectionParts = getPaginatedSectionParts(processedSections);

    return (
        <Document title={`Vehicle ${formTypeLabel} - ${metadata.vin}`}>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Image style={styles.headerBackground} src="/images/cr-pdf-header-gradient.png" />
                        <View style={styles.headerTitleContainer}>
                            <Text style={styles.headerTitle}>Vehicle {formTypeLabel}</Text>
                        </View>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.poweredByText}>Powered By</Text>
                        <Image style={styles.logo} src="/images/ParqAIlogo_cropped.png" />
                    </View>
                </View>

                <View style={styles.heroSection}>
                    <View style={styles.heroLeft}>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>Year, Make, Model</Text><Text style={styles.kvValue}>{`${metadata.year} ${metadata.make} ${metadata.model}`}</Text></View>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>VIN</Text><Text style={styles.kvValue}>{metadata.vin}</Text></View>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>Asset ID</Text><Text style={styles.kvValue}>{metadata.plate}</Text></View>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>Odometer</Text><Text style={styles.kvValue}>{metadata.mileage}</Text></View>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>Inspection Date</Text><Text style={styles.kvValue}>{metadata.date}</Text></View>
                        <View style={styles.kvItem}><Text style={styles.kvLabel}>Location</Text><Text style={styles.kvValue}>{metadata.location}</Text></View>
                    </View>
                    <View style={styles.heroRight}>
                        {heroImageObject?.url && (
                            <Link src={heroImageObject.url}>
                                <View style={styles.heroImageWrapper}>
                                    <Image style={styles.heroImage} src={heroImageObject.url} />
                                </View>
                            </Link>
                        )}
                    </View>
                </View>

                <View style={styles.summaryGrid}>
                    {processedSections.map(section => (
                        <View key={section.id} style={[styles.summaryCard, section.status === 'fail' ? styles.summaryFail : styles.summaryPass]}>
                            <Text>{`${section.name} — ${section.status.charAt(0).toUpperCase() + section.status.slice(1)}`}</Text>
                        </View>
                    ))}
                </View>

                {paginatedSectionParts[0].map((sectionPart, i) => <SectionPart key={i} sectionPart={sectionPart} />)}
            </Page>

            {
                paginatedSectionParts.slice(1).map((page, pageIndex) => (
                    <Page key={pageIndex} size="A4" style={styles.page}>
                        {
                            page.map((sectionPart, sectionPartIndex) => (
                                <SectionPart key={sectionPartIndex} sectionPart={sectionPart} />
                            ))
                        }
                    </Page>
                ))
            }

            {formType !== formTypes.Activity &&
                imagePages.map((pageImages, pageIndex) => (
                    <Page key={pageIndex} size="A4" style={styles.page}>
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>
                                Vehicle Images
                            </Text>
                            <View style={styles.generalImagesContainer}>
                                {pageImages.map((img, idx) => (
                                    <View key={idx} style={styles.generalImageWrapper}>
                                        <Link src={img.url} style={{ textDecoration: 'none' }}>
                                            <Image style={styles.image} src={img.url} />
                                            <Text style={styles.imageCaption}>{img.type}</Text>
                                        </Link>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </Page>
                ))}
        </Document>
    );
};

export default CustomFormOutputPDF;